import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { parentsApi, type ParentRegistrationPayload } from '@/api/endpoints/parents'
import { useUI } from '@/stores/ui'
import AppFooter from '@/components/common/AppFooter'

interface ChildInput {
  first_name: string
  last_name: string
  dob: string
  sacraments: { baptism: boolean; reconciliation: boolean; first_communion: boolean; confirmation: boolean }
  allergens: string
}

export default function ParentRegistrationPage() {
  const navigate = useNavigate()
  const addToast = useUI((state) => state.addToast)
  const params = useMemo(() => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : ''), [])
  const initialCode = params.get('code') || ''

  const [sharecode, setSharecode] = useState(initialCode.toUpperCase())
  const [parishName, setParishName] = useState<string | null>(null)
  const [validatingSharecode, setValidatingSharecode] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cellPhone, setCellPhone] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateCode, setStateCode] = useState('')
  const [zip, setZip] = useState('')
  // Unchecked by default: parents must actively attest (spec: "I am/am not a parishioner")
  const [isParishioner, setIsParishioner] = useState(false)
  const [useSameLastName, setUseSameLastName] = useState(true)
  const [emergencyPhones, setEmergencyPhones] = useState<string[]>([''])
  const [children, setChildren] = useState<ChildInput[]>([
    {
      first_name: '',
      last_name: '',
      dob: '',
      sacraments: { baptism: true, reconciliation: false, first_communion: false, confirmation: false },
      allergens: '',
    },
  ])
  const [submitting, setSubmitting] = useState(false)
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false)
  const [duplicateInfo, setDuplicateInfo] = useState<{ email?: string } | null>(null)

  // Validate sharecode when it changes
  useEffect(() => {
    const validateSharecode = async () => {
      if (!sharecode || sharecode.length !== 7) {
        setParishName(null)
        return
      }

      setValidatingSharecode(true)
      try {
        const parish = await parentsApi.validateSharecode(sharecode)
        if (parish) {
          setParishName(parish.name)
        } else {
          setParishName(null)
        }
      } catch (error) {
        setParishName(null)
      } finally {
        setValidatingSharecode(false)
      }
    }

    const timeoutId = setTimeout(validateSharecode, 500)
    return () => clearTimeout(timeoutId)
  }, [sharecode])

  // Auto-check sacrament options based on DOB when set
  useEffect(() => {
    setChildren(prev => prev.map(ch => {
      if (!ch.dob) return ch
      const age = getAge(ch.dob)
      const updated = {
        ...ch,
        sacraments: {
          baptism: ch.sacraments.baptism, // Keep user's choice
          reconciliation: age >= 7 ? (ch.sacraments.reconciliation || false) : ch.sacraments.reconciliation,
          first_communion: age >= 7 ? (ch.sacraments.first_communion || false) : ch.sacraments.first_communion,
          confirmation: age >= 12 ? (ch.sacraments.confirmation || false) : ch.sacraments.confirmation,
        },
      }
      // Auto-check if age-based and not already set
      if (age >= 7 && !ch.sacraments.reconciliation && !ch.sacraments.first_communion) {
        updated.sacraments.reconciliation = true
        updated.sacraments.first_communion = true
      }
      if (age >= 12 && !ch.sacraments.confirmation) {
        updated.sacraments.confirmation = true
      }
      return updated
    }))
  }, [children.map(ch => ch.dob).join('|')])

  const addChild = () => {
    if (children.length >= 20) {
      addToast({ message: 'Maximum 20 children per registration', type: 'warning' })
      return
    }
    setChildren(arr => [...arr, {
      first_name: '',
      last_name: useSameLastName && lastName ? lastName : '',
      dob: '',
      sacraments: { baptism: true, reconciliation: false, first_communion: false, confirmation: false },
      allergens: '',
    }])
  }

  // Update subsequent children's last names when useSameLastName is checked or parent lastName changes
  useEffect(() => {
    if (useSameLastName && lastName && children.length > 0) {
      setChildren(prev => prev.map((ch, idx) => 
        idx === 0 ? ch : { ...ch, last_name: lastName }
      ))
    }
  }, [useSameLastName, lastName])

  const removeChild = (idx: number) => {
    if (children.length === 1) {
      addToast({ message: 'At least one child is required', type: 'warning' })
      return
    }
    setChildren(arr => arr.filter((_, i) => i !== idx))
  }

  const addEmergencyPhone = () => {
    if (emergencyPhones.length >= 10) {
      addToast({ message: 'Maximum 10 emergency contacts', type: 'warning' })
      return
    }
    setEmergencyPhones(arr => [...arr, ''])
  }

  const removeEmergencyPhone = (idx: number) => {
    setEmergencyPhones(arr => arr.length > 1 ? arr.filter((_, i) => i !== idx) : [''])
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation with specific error messages
    const errors: string[] = []

    if (!sharecode || sharecode.length !== 7) {
      errors.push('Please enter a valid 7-digit sharecode')
    }

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters')
    }

    if (!email.trim()) {
      errors.push('Email is required')
    } else if (!email.includes('@')) {
      errors.push('Please enter a valid email address')
    }

    if (!firstName.trim()) {
      errors.push('Parent first name is required')
    }

    if (!lastName.trim()) {
      errors.push('Parent last name is required')
    }

    const validChildren = children.filter(ch => ch.first_name.trim() && ch.last_name.trim() && ch.dob)
    if (validChildren.length === 0) {
      errors.push('Please add at least one child with complete information (first name, last name, and date of birth)')
    } else {
      // Check each child for missing fields
      children.forEach((ch, idx) => {
        if (!ch.first_name.trim()) {
          errors.push(`Child ${idx + 1}: First name is required`)
        }
        if (!ch.last_name.trim()) {
          errors.push(`Child ${idx + 1}: Last name is required`)
        }
        if (!ch.dob) {
          errors.push(`Child ${idx + 1}: Date of birth is required`)
        }
      })
    }

    if (errors.length > 0) {
      // Show first error, or all errors if there are 3 or fewer
      const errorMessage = errors.length <= 3 
        ? errors.join('. ') 
        : `${errors[0]}. Please check all required fields.`
      addToast({ message: errorMessage, type: 'error' })
      return
    }

    setSubmitting(true)
    try {
      // Prepare payload
      const payload: ParentRegistrationPayload = {
        email: email.trim(),
        password: password.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        cell_phone: cellPhone.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state: stateCode.trim() || undefined,
        zip_code: zip.trim() || undefined,
        emergency_phone_numbers: emergencyPhones.filter(p => p.trim()).map(p => p.trim()),
        is_parishioner: isParishioner,
        parish_sharecode: sharecode.toUpperCase(),
        children: validChildren.map(ch => ({
          first_name: ch.first_name.trim(),
          last_name: ch.last_name.trim(),
          date_of_birth: ch.dob,
          sacraments_received: [
            ...(ch.sacraments.baptism ? ['baptism'] : []),
            ...(ch.sacraments.reconciliation ? ['reconciliation'] : []),
            ...(ch.sacraments.first_communion ? ['first_communion'] : []),
            ...(ch.sacraments.confirmation ? ['confirmation'] : []),
          ],
          allergen_alerts: ch.allergens.trim() ? ch.allergens.trim().split(',').map(a => a.trim()) : [],
          is_parishioner: isParishioner,
        })),
      }

      const response = await parentsApi.register(payload)

      // Check for duplicate detection
      if (response.potential_duplicate) {
        setDuplicateInfo({ email: response.existing_email })
        setShowDuplicatePrompt(true)
        return
      }

      // Success
      addToast({ 
        message: 'Registration submitted successfully! You will receive an email once approved.', 
        type: 'success' 
      })
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', { state: { message: 'Registration submitted. Please wait for approval.' } })
      }, 2000)
    } catch (error: any) {
      console.error('Registration error:', error)
      const errorMessage = error.response?.data?.detail || 'Failed to submit registration. Please try again.'
      addToast({ message: errorMessage, type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDuplicateChoice = async (useExisting: boolean) => {
    setShowDuplicatePrompt(false)
    if (useExisting) {
      addToast({ 
        message: 'Please log in with your existing account or contact the parish administrator.', 
        type: 'info' 
      })
      navigate('/login')
    } else {
      // User wants to continue with new registration
      // Re-submit with a flag or continue anyway
      addToast({ 
        message: 'If you have an existing account, please contact the parish administrator to merge accounts.', 
        type: 'warning' 
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 page-transition">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          className="bg-white rounded-2xl shadow-xl p-6 sm:p-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="mb-6 pb-4 border-b-2 border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Parent Registration</h1>
            <p className="text-sm text-gray-600">
              Register your children for catechism classes. All fields marked with <span className="text-red-500">*</span> are required.
            </p>
            {initialCode && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  📍 Sharecode pre-filled from registration link. Please verify it matches your parish.
                </p>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="space-y-6">
            {/* Parish Sharecode */}
            <section className="card p-5 rounded-xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Parish Sharecode <span className="text-red-500">*</span></h2>
              <p className="text-xs text-gray-500 mb-3">
                Don't have a Parish sharecode? Be sure to get one from your Parish to register!
              </p>
              <div className="space-y-2">
                <input
                  data-testid="sharecode-input"
                  className="input-field w-full focus-ring text-lg font-mono tracking-wider uppercase"
                  placeholder="ABC1234"
                  value={sharecode}
                  onChange={(e) => setSharecode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7))}
                  maxLength={7}
                  required
                  aria-required="true"
                  aria-label="Parish sharecode"
                />
                {sharecode.length === 7 && (
                  <div className="space-y-1">
                    {validatingSharecode ? (
                      <p className="text-xs text-gray-500">🔍 Validating sharecode...</p>
                    ) : parishName ? (
                      <p className="text-xs text-green-600 font-medium">✅ {parishName}</p>
                    ) : (
                      <p className="text-xs text-gray-500">Sharecode will be validated on submission</p>
                    )}
                  </div>
                )}
                {sharecode.length > 0 && sharecode.length !== 7 && (
                  <p className="text-xs text-yellow-600">⚠️ Sharecode must be exactly 7 characters</p>
                )}
                {parishName && !validatingSharecode && sharecode.length === 7 && (
                  <p className="text-sm text-gray-700 font-medium mt-2">Parish: {parishName}</p>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                💡 Need to register in multiple parishes? Complete this registration for one parish, then register again with that parish's sharecode.
              </p>
            </section>

            {/* Parent Details */}
            <section className="card p-5 rounded-xl border-2 border-gray-200 bg-white">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Parent/Guardian Information</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="parent-email-input"
                    className="input-field w-full focus-ring"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      data-testid="parent-password-input"
                      className="input-field w-full focus-ring pr-10"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      aria-required="true"
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus-ring"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {password && password.length < 8 && (
                    <p className="text-xs text-yellow-600 mt-1">Password must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="parent-first-name-input"
                    className="input-field w-full focus-ring"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="parent-last-name-input"
                    className="input-field w-full focus-ring"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Cell Phone</label>
                  <input
                    data-testid="parent-phone-input"
                    className="input-field w-full focus-ring"
                    type="tel"
                    value={cellPhone}
                    onChange={(e) => setCellPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                <div className="sm:col-span-3">
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Street Address</label>
                  <input
                    data-testid="parent-address-input"
                    className="input-field w-full focus-ring"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold mb-2 text-gray-700">City</label>
                  <input
                    data-testid="parent-city-input"
                    className="input-field w-full focus-ring"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">State</label>
                  <input
                    data-testid="parent-state-input"
                    className="input-field w-full focus-ring"
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="WY"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">ZIP Code</label>
                  <input
                    data-testid="parent-zip-input"
                    className="input-field w-full focus-ring"
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="12345"
                  />
                </div>
              </div>

              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    id="parishioner"
                    type="checkbox"
                    className="mt-1 w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                    checked={isParishioner}
                    onChange={(e) => setIsParishioner(e.target.checked)}
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900 block">
                      I attest that I am a parishioner of {parishName || 'this parish'}
                    </span>
                    <span className="text-xs text-gray-600 mt-1 block">
                      Not required to proceed, but helps your parish administrator verify your registration
                    </span>
                  </div>
                </label>
              </div>

              {/* Emergency Contacts */}
              <div className="mt-4">
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Additional Emergency Contact Phone Numbers <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Add emergency contacts (friends, family, neighbors) who can be reached if you're unavailable
                </p>
                {emergencyPhones.map((phone, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      className="input-field flex-1 focus-ring"
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        const updated = [...emergencyPhones]
                        updated[i] = e.target.value
                        setEmergencyPhones(updated)
                      }}
                      placeholder="(555) 123-4567"
                    />
                    <button
                      type="button"
                      className="btn-secondary focus-ring"
                      onClick={() => removeEmergencyPhone(i)}
                      disabled={emergencyPhones.length === 1}
                      aria-label="Remove emergency contact"
                    >
                      ➖
                    </button>
                    {i === emergencyPhones.length - 1 && emergencyPhones.length < 10 && (
                      <button
                        type="button"
                        className="btn-secondary focus-ring"
                        onClick={addEmergencyPhone}
                        aria-label="Add emergency contact"
                      >
                        ➕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Password Note */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📝 Important:</strong> Save your password. Once your registration has been approved, you will be able to log in to view your messages and your child's class schedule, assignments, etc. You can reset your password as well, once your registration has been approved. You will receive a notification email once your registration has been approved - be patient!
              </p>
            </div>

            {/* Children Section */}
            <section className="card p-5 rounded-xl border-2 border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Children</h2>
                  <p className="text-xs text-gray-500 mt-1">Add all children you wish to register (at least one required)</p>
                </div>
                <button
                  data-testid="add-child-button"
                  type="button"
                  className="btn-secondary text-sm focus-ring"
                  onClick={addChild}
                  disabled={children.length >= 20}
                >
                  ➕ Add Child
                </button>
              </div>

              {children.map((ch, idx) => {
                const age = ch.dob ? getAge(ch.dob) : null
                return (
                  <motion.div
                    key={idx}
                    className="rounded-xl border-2 border-gray-200 p-4 mb-4 bg-gradient-to-br from-gray-50 to-white"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                      <div className="text-sm font-semibold text-gray-700">Child {idx + 1}</div>
                      <button
                        type="button"
                        className="btn-secondary text-xs focus-ring"
                        onClick={() => removeChild(idx)}
                        disabled={children.length === 1}
                        aria-label={`Remove child ${idx + 1}`}
                      >
                        ➖ Remove
                      </button>
                    </div>

                    {idx === 0 && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-500 focus-ring rounded border-gray-300"
                            checked={useSameLastName}
                            onChange={(e) => setUseSameLastName(e.target.checked)}
                          />
                          <span className="text-sm text-gray-900">
                            Use the same last name for all children
                          </span>
                        </label>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          data-testid={`child-${idx}-first-name-input`}
                          className="input-field w-full focus-ring"
                          value={ch.first_name}
                          onChange={(e) => {
                            const updated = [...children]
                            updated[idx] = { ...updated[idx], first_name: e.target.value }
                            setChildren(updated)
                          }}
                          required={idx === 0}
                          aria-required={idx === 0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          data-testid={`child-${idx}-last-name-input`}
                          className="input-field w-full focus-ring"
                          value={ch.last_name}
                          onChange={(e) => {
                            const updated = [...children]
                            updated[idx] = { ...updated[idx], last_name: e.target.value }
                            setChildren(updated)
                          }}
                          required={idx === 0}
                          aria-required={idx === 0}
                          disabled={idx > 0 && useSameLastName}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          data-testid={`child-${idx}-dob-input`}
                          type="date"
                          className="input-field w-full focus-ring"
                          value={ch.dob}
                          onChange={(e) => {
                            const updated = [...children]
                            updated[idx] = { ...updated[idx], dob: e.target.value }
                            setChildren(updated)
                          }}
                          max={new Date().toISOString().split('T')[0]}
                          required={idx === 0}
                          aria-required={idx === 0}
                        />
                        {ch.dob && age !== null && (
                          <p className="text-xs text-gray-500 mt-1">
                            Age: {age} {age === 1 ? 'year' : 'years'} old
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Sacraments */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Sacraments Received</label>
                      {age !== null && age >= 7 && (
                        <p className="text-xs text-gray-500 mb-2">
                          💡 Based on age, some sacraments are pre-selected. You can uncheck if needed.
                        </p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                            checked={ch.sacraments.baptism}
                            onChange={(e) => {
                              const updated = [...children]
                              updated[idx] = {
                                ...updated[idx],
                                sacraments: { ...updated[idx].sacraments, baptism: e.target.checked }
                              }
                              setChildren(updated)
                            }}
                          />
                          <span className="text-sm">Baptism</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                            checked={ch.sacraments.reconciliation}
                            onChange={(e) => {
                              const updated = [...children]
                              updated[idx] = {
                                ...updated[idx],
                                sacraments: { ...updated[idx].sacraments, reconciliation: e.target.checked }
                              }
                              setChildren(updated)
                            }}
                            disabled={age !== null && age < 7}
                          />
                          <span className={`text-sm ${age !== null && age < 7 ? 'text-gray-400' : ''}`}>
                            Reconciliation {age !== null && age >= 7 && <span className="text-yellow-600">✓</span>}
                          </span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                            checked={ch.sacraments.first_communion}
                            onChange={(e) => {
                              const updated = [...children]
                              updated[idx] = {
                                ...updated[idx],
                                sacraments: { ...updated[idx].sacraments, first_communion: e.target.checked }
                              }
                              setChildren(updated)
                            }}
                            disabled={age !== null && age < 7}
                          />
                          <span className={`text-sm ${age !== null && age < 7 ? 'text-gray-400' : ''}`}>
                            First Communion {age !== null && age >= 7 && <span className="text-yellow-600">✓</span>}
                          </span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                            checked={ch.sacraments.confirmation}
                            onChange={(e) => {
                              const updated = [...children]
                              updated[idx] = {
                                ...updated[idx],
                                sacraments: { ...updated[idx].sacraments, confirmation: e.target.checked }
                              }
                              setChildren(updated)
                            }}
                            disabled={age !== null && age < 12}
                          />
                          <span className={`text-sm ${age !== null && age < 12 ? 'text-gray-400' : ''}`}>
                            Confirmation {age !== null && age >= 12 && <span className="text-yellow-600">✓</span>}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Allergen Alerts */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Allergen Alerts</label>
                      <input
                        className="input-field w-full focus-ring"
                        placeholder="e.g., peanuts, gluten, dairy (comma-separated)"
                        value={ch.allergens}
                        onChange={(e) => {
                          const updated = [...children]
                          updated[idx] = { ...updated[idx], allergens: e.target.value }
                          setChildren(updated)
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        List any food allergies or dietary restrictions for this child
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        <strong>Note:</strong> By signing up, you agree that you and your child are ultimately responsible for ensuring the child is not exposed to reactive allergens.
                      </p>
                    </div>
                  </motion.div>
                )
              })}
            </section>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t-2 border-gray-200">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-gray-900 underline focus-ring"
              >
                Already registered? Log in here
              </Link>
              <button
                data-testid="submit-registration-button"
                type="submit"
                className="btn-primary focus-ring min-w-[200px]"
                disabled={submitting}
                aria-label="Submit registration"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin-smooth">⏳</span>
                    <span>Submitting…</span>
                  </span>
                ) : (
                  '✨ Submit Registration'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Duplicate Prompt Modal */}
      <AnimatePresence>
        {showDuplicatePrompt && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-modal="true"
            role="dialog"
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4 hover-raise"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="text-3xl">⚠️</span>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Possible Duplicate Registration</h3>
                  <p className="text-sm text-gray-700">
                    It appears you may have registered previously. We found a matching registration{duplicateInfo?.email && ` with email: ${duplicateInfo.email}`}.
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Would you like to use your existing account, or continue with a new registration? If you have questions, please contact your parish administrator.
              </p>
              <div className="flex gap-3">
                <button
                  className="btn-secondary flex-1 focus-ring"
                  onClick={() => handleDuplicateChoice(true)}
                >
                  Use Existing Account
                </button>
                <button
                  className="btn-primary flex-1 focus-ring"
                  onClick={() => handleDuplicateChoice(false)}
                >
                  Continue New Registration
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AppFooter />
    </div>
  )
}

function getAge(dob: string): number {
  if (!dob) return 0
  const d = new Date(dob)
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--
  return age
}
